import useHttpProvider from "@/common/hooks/use-http";
import { notifyError, notifySuccess } from "@/components/ui/sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import * as AuthenticationService from "../services/authentication";

const ForgotPasswordFormSchema = z.object({
	email: z.string().email("Enter a valid email"),
});

const passwordPattern = /^(?=.*\d).{8,}$/;

const ResetPasswordFormSchema = z.object({
	email: z.string().email("Enter a valid email"),
	otpCode: z
		.string()
		.min(6, "OTP code is required")
		.max(6, "OTP code is invalid"),
	password: z
		.string({
			message: "Password is required",
		})
		.min(8, "Password must be at least 8 characters long")
		.regex(passwordPattern, "Password must contain at least one number"),
});

function useForgotPassword() {
	const { http } = useHttpProvider();

	const forgotPasswordForm = useForm<z.infer<typeof ForgotPasswordFormSchema>>({
		resolver: zodResolver(ForgotPasswordFormSchema),
	});

	const resetPasswordForm = useForm<z.infer<typeof ResetPasswordFormSchema>>({
		resolver: zodResolver(ResetPasswordFormSchema),
	});

	const [showForgotPasswordDialog, setShowForgotPasswordDialog] =
		useState(false);
	const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);

	async function onForgotPasswordSubmit(
		data: z.infer<typeof ForgotPasswordFormSchema>
	) {
		try {
			const email = data.email;
			await AuthenticationService.requestForgotPassword(http, data.email);

			setShowForgotPasswordDialog(false);
			setShowResetPasswordDialog(true);

			resetPasswordForm.setValue("email", email);

			notifySuccess(
				"Authentication",
				"An OTP code has been sent to your email."
			);
		} catch {
			notifyError("Uh oh! Something went wrong.", "Failed to send request.");
		}
	}

	async function onResetPasswordSubmit(
		data: z.infer<typeof ResetPasswordFormSchema>
	) {
		try {
			const { email, otpCode, password } = data;
			await AuthenticationService.confirmForgotPassword(
				http,
				email,
				otpCode,
				password
			);

			setShowResetPasswordDialog(false);
			notifySuccess("Authentication", "Password has been reset.");
		} catch {
			notifyError("Uh oh! Something went wrong.", "Failed to reset password.");
		}
	}

	useEffect(() => {
		if (!showForgotPasswordDialog) forgotPasswordForm.reset();
	}, [showForgotPasswordDialog]);

	useEffect(() => {
		if (!showResetPasswordDialog) resetPasswordForm.reset();
	}, [showResetPasswordDialog]);

	return {
		forgotPasswordForm,
		onForgotPasswordSubmit,
		showForgotPasswordDialog,
		setShowForgotPasswordDialog,

		resetPasswordForm,
		onResetPasswordSubmit,
		showResetPasswordDialog,
		setShowResetPasswordDialog,
	};
}

export default useForgotPassword;
